import config from '@/config'

class RecaptchaV3Service {
  constructor() {
    this.isLoaded = false
    this.isLoading = false
    this.loadPromise = null
  }

  /**
   * Load reCAPTCHA v3 script
   */
  async loadRecaptcha() {
    if (this.isLoaded) {
      return Promise.resolve()
    }

    if (this.isLoading) {
      return this.loadPromise
    }

    this.isLoading = true
    this.loadPromise = new Promise((resolve, reject) => {
      try {
        // Check if already loaded
        if (this.isLoaded && window.grecaptcha && window.grecaptcha.execute) {
          this.isLoaded = true
          this.isLoading = false
          resolve()
          return
        }
        // Create script element
        const script = document.createElement('script')
        script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${config.recaptchaV3Key}`
        script.async = true
        script.defer = true

        script.onload = () => {
          // Wait for grecaptcha to be ready
          const checkReady = () => {
            if (window.grecaptcha && window.grecaptcha.execute) {
              this.isLoaded = true
              this.isLoading = false
              resolve()
            } else {
              setTimeout(checkReady, 100)
            }
          }
          checkReady()
        }

        script.onerror = () => {
          this.isLoading = false
          reject(new Error('Failed to load reCAPTCHA script'))
        }

        document.head.appendChild(script)
      } catch (error) {
        this.isLoading = false
        reject(error)
      }
    })

    return this.loadPromise
  }

  /**
   * Execute reCAPTCHA v3 and get token
   * @param {string} action - The action name (e.g., 'login', 'forgot_password')
   * @returns {Promise<string>} reCAPTCHA token
   */
  async executeRecaptcha(action = 'submit') {
    try {
      await this.loadRecaptcha()
    } catch {
      return null
    }
    if (!window.grecaptcha || !window.grecaptcha.execute) {
      throw new Error('reCAPTCHA not loaded')
    }

    // eslint-disable-next-line sonarjs/prefer-immediate-return
    const token = await window.grecaptcha.execute(config.recaptchaV3Key, {
      action,
    })

    return token
  }
}

// Create singleton instance
const recaptchaV3Service = new RecaptchaV3Service()

export default recaptchaV3Service
